<template>
  <div>
    <div class="relative w-full mb-12">
      <h2 class="block-heading font-medium text-los-blue-darkest">
        <slot name="title">Onze trainingen</slot>
      </h2>
      <CarouselContainer
        v-if="!workshopsListEmpty"
        :ariaLabel="`Geplande trainingen.`"
      >
        <WorkshopFrontItem
          v-for="workshop in workshopsSorted"
          :key="workshop.id"
          :workshop="workshop"
          :type-name="typeName"
          :active="workshop.id === activeWorkshopId"
          tabindex="0"
          :aria-expanded="workshop.id === activeWorkshopId"
          aria-controls="inschrijf-formulier"
          class="cursor-pointer shrink-0"
          @click="toggleActiveWorkshop(workshop.id)"
          @keydown.enter="toggleActiveWorkshop(workshop.id)"
        />
      </CarouselContainer>

      <p class="text-basic italic" v-else>
        Er staat op dit moment nog geen {{ typeName }} op de agenda. We hopen
        snel weer nieuwe trainingen in te plannen.
      </p>
    </div>

    <Transition
      mode="out-in"
      enter-active-class="transition ease-out duration-200 opacity-0 "
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <WorkshopApplyContainer
        v-if="activeWorkshop"
        :key="activeWorkshopId"
        @close="toggleActiveWorkshop(activeWorkshopId)"
      >
        <WorkshopApplyForm
          :workshop="{
            id: activeWorkshop.id,
            ...activeWorkshop,
          }"
          :typeName="typeName"
          :showCol="showCol"
          :formConfig="formConfig"
          @onApply="removeWorkshopSpot"
        >
          <slot name="formContent"></slot>
        </WorkshopApplyForm>
      </WorkshopApplyContainer>
    </Transition>
  </div>
</template>

<script setup>
import dayjs from 'dayjs';

const activeWorkshopId = ref(null);
const localWorkshopSpots = ref([]);

const props = defineProps({
  workshops: {
    type: Array,
    default: () => [],
  },
  activeWorkshopId: {
    type: String,
    default: null,
  },
  typeName: {
    type: String,
    default: 'workshop',
  },
  showCol: {
    type: Boolean,
    default: false,
  },
  formConfig: {
    type: Object,
    default: null,
  },
});

const workshopsSorted = computed(() => {
  return props.workshops
    .map((w) => {
      if (localWorkshopSpots.value.includes(w.id)) {
        w.participants.push({ id: -1 });
      }
      return w;
    })
    .filter((w) => {
      return dayjs().isSameOrBefore(w.start_date_time, 'day');
    })
    .sort((a, b) => new Date(a.start_date_time) - new Date(b.start_date_time));
});

const activeWorkshop = computed(() => {
  return props.workshops.find(({ id }) => id === activeWorkshopId.value);
});

const workshopsListEmpty = computed(() => {
  return workshopsSorted.value.length < 1;
});

function toggleActiveWorkshop(id) {
  if (id === activeWorkshopId.value) {
    activeWorkshopId.value = null;
  } else {
    activeWorkshopId.value = id;
    nextTick(scrollToForm);
  }
}

function removeWorkshopSpot(workshopId) {
  localWorkshopSpots.value.push(workshopId);
}

onMounted(() => {
  if (props.activeWorkshopId) {
    activeWorkshopId.value = Number(props.activeWorkshopId);
    nextTick(scrollToForm);
  }
});

function scrollToForm() {
  setTimeout(() => {
    const element = document.getElementById('inschrijf-formulier');
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
    setTimeout(() => {
      const input = document.getElementById('firstname');
      input?.focus();
    }, 1);
  }, 200);
}
</script>

<style lang="scss" scoped></style>
